import axios from 'axios';
import { API_CONFIG } from '@/config/api.config';
import { encryptData, decryptData } from '@/utils/encryption';

// ===========================================
// USPTO Forms API (RCE, etc.)
// ===========================================

export interface Signature {
  id?: string;
  name: string;
  registrationNumber: string;
  label?: string;
  // No S-signature text. 37 CFR 1.4(d)(2) requires the signer to insert their own
  // S-signature personally, so no form this tool generates fills that line in.
}

export interface RceFormData {
  appNo: string;
  signature?: Signature;
}

export interface IdsFormData {
  appNo: string;
  signature?: Signature;
}

export type TerminalDisclaimerFormType =
  | 'pending_vs_pending'
  | 'pending_vs_prior_patent'
  | 'design_petition'
  | 'patent_vs_application'
  | 'patent_vs_patent';

export interface TerminalDisclaimerFormData {
  appNo: string;
  tdFormType?: TerminalDisclaimerFormType;
  referenceNo?: string;
  referenceFilingDate?: string;
  signature?: Signature;
}

export interface NoticeOfAppealFormData {
  appNo: string;
  signature?: Signature;
}

export interface PoaTransmittalFormData {
  appNo: string;
  signature?: Signature & { title?: string };
  customerNumber?: string;
}

export interface TransmittalFormData {
  appNo: string;
  signature?: Signature;
  totalPages?: string;
}

export interface JobStatus {
  status: 'pending' | 'processing' | 'complete' | 'error' | 'failed';
  fileKey?: string;
  error_message?: string;
  error_type?: string;
}

export const formsApi = {
  /**
   * Submit RCE form for async PDF generation
   */
  async submitRceForm(formData: RceFormData): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        ...formData,
        formType: 'rce',
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/rce/generate`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id || result.jobId;
    } catch (error: any) {
      console.error('Failed to submit RCE form:', error);
      throw error;
    }
  },

  /**
   * Submit IDS blank form for async PDF generation
   * This only fills in application data and signature - not citations
   */
  async submitIdsForm(formData: IdsFormData): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        ...formData,
        formType: 'ids_blank',
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/ids/generate`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id || result.jobId;
    } catch (error: any) {
      console.error('Failed to submit IDS form:', error);
      throw error;
    }
  },

  /**
   * Submit Terminal Disclaimer form for async PDF generation
   *
   * Form types:
   * - pending_vs_pending: Pending app vs pending reference app (default)
   * - pending_vs_prior_patent: Pending app vs prior issued patent
   * - design_petition: Design app with petition under 37 CFR 1.137
   * - patent_vs_application: Issued patent vs pending application
   * - patent_vs_patent: Issued patent vs another issued patent
   */
  async submitTerminalDisclaimerForm(formData: TerminalDisclaimerFormData): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        appNo: formData.appNo,
        tdFormType: formData.tdFormType || 'pending_vs_pending',
        referenceNo: formData.referenceNo,
        referenceFilingDate: formData.referenceFilingDate,
        signature: formData.signature,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/terminal-disclaimer/generate`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id || result.jobId;
    } catch (error: any) {
      console.error('Failed to submit Terminal Disclaimer form:', error);
      throw error;
    }
  },

  /**
   * Submit Notice of Appeal form for async PDF generation
   */
  async submitNoticeOfAppealForm(formData: NoticeOfAppealFormData): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        appNo: formData.appNo,
        signature: formData.signature,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/notice-of-appeal/generate`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id || result.jobId;
    } catch (error: any) {
      console.error('Failed to submit Notice of Appeal form:', error);
      throw error;
    }
  },

  /**
   * Submit POA Transmittal form for async PDF generation
   */
  async submitPoaTransmittalForm(formData: PoaTransmittalFormData): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        appNo: formData.appNo,
        signature: formData.signature,
        customerNumber: formData.customerNumber,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/poa-transmittal/generate`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id || result.jobId;
    } catch (error: any) {
      console.error('Failed to submit POA Transmittal form:', error);
      throw error;
    }
  },

  /**
   * Submit Transmittal Form for async PDF generation
   */
  async submitTransmittalForm(formData: TransmittalFormData): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        appNo: formData.appNo,
        signature: formData.signature,
        totalPages: formData.totalPages,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/transmittal/generate`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id || result.jobId;
    } catch (error: any) {
      console.error('Failed to submit Transmittal form:', error);
      throw error;
    }
  },

  /**
   * Poll job status for form generation
   */
  async getJobStatus(jobId: string): Promise<JobStatus> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        jobId,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/job_status`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to get form job status:', error);
      throw error;
    }
  },

  /**
   * Wait for job completion with polling
   */
  async waitForJobCompletion(
    jobId: string,
    onProgress?: (status: JobStatus) => void,
    timeout = 120000
  ): Promise<JobStatus> {
    const startTime = Date.now();
    const pollInterval = 2000;

    while (Date.now() - startTime < timeout) {
      const status = await this.getJobStatus(jobId);

      if (onProgress) {
        onProgress(status);
      }

      if (status.status === 'complete') {
        return status;
      }

      if (status.status === 'error' || status.status === 'failed') {
        throw new Error(status.error_message || 'Form generation failed');
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    throw new Error('Form generation timed out');
  },

  /**
   * Download completed PDF file
   */
  async downloadFile(fileKey: string): Promise<Blob> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        file_key: fileKey,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/forms/download`,
        { data: encryptedData },
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to download form file:', error);
      throw error;
    }
  },

  /**
   * Get user's signatures (shared with IDS)
   * Fetches from IDS settings since signatures are shared
   */
  async getSignatures(): Promise<{ signatures: Signature[]; defaultSignatureId?: string }> {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/ids/settings`,
        {
          params: { id_token: idToken },
        }
      );
      const settings = response.data || {};
      return {
        signatures: settings.signatures || [],
        defaultSignatureId: settings.defaultSignatureId,
      };
    } catch (error: any) {
      console.error('Failed to get signatures:', error);
      // Return empty array on error - signatures are optional
      return { signatures: [] };
    }
  },

  /**
   * Update signatures (saves to IDS settings since signatures are shared)
   */
  async updateSignatures(signatures: Signature[], defaultSignatureId?: string): Promise<void> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        signatures,
        defaultSignatureId,
        id_token: idToken,
      });
      await axios.post(
        `${API_CONFIG.baseUrl}/ids/settings/update`,
        { data: encryptedData }
      );
    } catch (error: any) {
      console.error('Failed to update signatures:', error);
      throw error;
    }
  },
};
